import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useModal } from "../../../../utils/custom-hooks";
import { ApplicationList } from "../../../organisms/smart-apply/application-list";
import { SmartApplyMenuList } from "../../../organisms/smart-apply/menu-list";
import { SmartApplyTemplate } from "../../../template/smart-apply/default-template";
import { ApplicationEntity } from "../../../../application/domain/application/application-entity";
import {
  gqlClient,
  gqlHeader,
  runGqlQueryV2,
  Sort,
} from "@graffer-inc/smart-apply";
import { ApplicationFactory } from "../../../../application/domain/application/application-factory";
import { ApplicantSchemaKeys, ApplyStatus } from "@graffer-inc/cloud-gov-core";
import { emptyFunction } from "../../../../utils/function-utils";
import { useFeatureFlag_EnableApplyAssignment } from "../../../../feature-flags";
import { AssignedProcedureSelect } from "../../../organisms/smart-apply/assigned-procedure-select";
import {
  parseQueryOfLocation,
  rebuildQuery,
} from "../../../../utils/url-utils";
import { useHistory, useLocation } from "react-router-dom";
import {
  ContentsHeader,
  DateRangeInput,
  InformationBanner,
  Pagination,
} from "@graffer-inc/console-design-kit";
import {
  DefaultNumItemsPerPages,
  paging,
  toTotalPage,
} from "../../../../application/domain/pagination/paging";
import { reportUnexpectedError } from "../../../../error-reporter";
import { formatDate, parseDate } from "../../../../utils/date-utils";
import { useAuthContextV2 } from "../../../../auth-state";
import { matchType } from "../../../../application/domain/base/base-type";

const applicationCountPerPage = DefaultNumItemsPerPages;

const useAssignedApplications = (
  page: number,
  procId?: string,
  fromDate?: Date,
  toDate?: Date,
  statuses?: ApplyStatus[],
) => {
  const [applications, setApplications] = useState<
    {
      application: ApplicationEntity;
      applicantSchemaKeys: ApplicantSchemaKeys;
    }[]
  >([]);
  const [paging, setPaging] = useState<paging>({ current: 1, total: 1 });
  const [procOpts, setProcOpts] = useState<{ label: string; value: string }[]>(
    [],
  );

  const fetchApplications = (
    page: number,
    procedureId?: string,
    fromDate?: Date,
    toDate?: Date,
    statuses?: ApplyStatus[],
  ) => {
    const params = {
      page: page,
      limit: applicationCountPerPage,
      procedureId: procedureId,
      from: fromDate ? formatDate(fromDate) : undefined,
      to: toDate ? formatDate(toDate) : undefined,
      sort: "desc" as Sort,
      status: statuses,
    };
    runGqlQueryV2(() =>
      gqlClient.getAssignedApplications(params, gqlHeader("q")),
    ).then((res) => {
      if (res.data) {
        const { applications, totalCount } = res.data.assignedApplications;

        const aplsWithSchemaKey = applications.map(
          ({ application, applicantSchemaKeys }) => ({
            application: ApplicationFactory.createFromGql(application),
            applicantSchemaKeys,
          }),
        );
        setApplications(aplsWithSchemaKey);
        setPaging({
          current: page,
          total: toTotalPage(totalCount, applicationCountPerPage),
        });
      }
    });
  };

  const searchProcedures = async (
    text: string,
    setOptCallback: (opts: Array<{ label: string; value: string }>) => void,
  ) => {
    return runGqlQueryV2(() =>
      gqlClient.assignedApplicationsProcedures(
        {
          name: text,
        },
        gqlHeader("q"),
      ),
    )
      .then((res) => {
        if (res.data) {
          const { procedures } = res.data.assignedApplicationsProcedures;
          const opts = procedures.map((p) => ({
            label: p.name,
            value: p.id,
          }));
          setOptCallback(opts);
        }
      })
      .catch((e) => reportUnexpectedError(e));
  };

  useEffect(() => {
    fetchApplications(page, procId, fromDate, toDate, statuses);
  }, [page, procId, fromDate, toDate, statuses]);

  useEffect(() => {
    searchProcedures("", setProcOpts);
  }, []);

  return {
    applications: applications,
    paging: paging,
    procOpts: procOpts,
    searchProcedures: searchProcedures,
  };
};

export const AssignedApplications: React.FC = () => {
  const {
    page: currentPage,
    procedureId,
    from,
    to,
    status,
  } = parseQueryOfLocation(useLocation()) as {
    page: string | undefined;
    procedureId: string | undefined;
    from: string | undefined;
    to: string | undefined;
    status: string | undefined;
  };

  const [dateFrom, dateTo] = useMemo(() => {
    const parsedFrom = from ? parseDate(from) : undefined;
    const parsedTo = to ? parseDate(to) : undefined;
    return [parsedFrom, parsedTo];
  }, [from, to]);
  const statuses: Array<ApplyStatus> = useMemo(() => {
    return status ? JSON.parse(status) : [];
  }, [status]);

  const history = useHistory();
  const onSelectProcedure = (procedureId?: string) => {
    history.push(
      rebuildQuery({
        page: "1",
        procedureId: procedureId ?? "",
      }),
    );
  };
  const onSetDateFilter = useCallback(
    ({
      startDate,
      endDate,
    }: {
      startDate: Date | null;
      endDate: Date | null;
    }) => {
      const from = startDate ? formatDate(startDate) : undefined;
      const to = endDate ? formatDate(endDate) : undefined;
      history.push(rebuildQuery({ page: "1", from: from, to: to }));
    },
    [history],
  );
  const onSelectStatuses = useCallback(
    (statuses: ApplyStatus[]) => {
      history.push(rebuildQuery({ page: "1", status: statuses }));
    },
    [history],
  );
  const onSetPage = (page: number) => {
    history.push(rebuildQuery({ page: page.toString() }));
  };

  const { auth } = useAuthContextV2();
  const { applications, paging, procOpts, searchProcedures } =
    useAssignedApplications(
      parseInt(currentPage || "1", 10),
      procedureId,
      dateFrom,
      dateTo,
      statuses,
    );

  const downloadCsvModal = useModal();
  const statusUpdateModal = useModal();
  const csvUploadModal = useModal();
  const pdfGenerateModal = useModal();
  const bulkDownloadFilesModal = useModal();

  if (!useFeatureFlag_EnableApplyAssignment(auth.orgSlug)) {
    return null;
  }

  return (
    <SmartApplyTemplate
      breadcrumbs={[{ to: "#", title: "振り分け申請一覧" }]}
      menuList={<SmartApplyMenuList />}
    >
      <ContentsHeader
        title={"振り分け申請一覧"}
        caption={[
          <div
            key={"assignedApplicationsProcedureSelect"}
            className="assigned-applications-procedure-select"
            data-testid="procedure-name"
            data-for-rpa-id={"select-procedure"}
            style={{
              width: 320,
            }}
          >
            <AssignedProcedureSelect
              selectedProcId={procedureId}
              searchProcedures={searchProcedures}
              defaultProcedureOpts={procOpts}
              onSelect={onSelectProcedure}
            />
          </div>,
          <DateRangeInput
            key={"dateRangeInput"}
            startDate={dateFrom}
            endDate={dateTo}
            setValue={onSetDateFilter}
          />,
        ]}
        captionDirection="row"
      />
      <div>
        <ApplicationList
          isLoading={false}
          appls={applications}
          selectable={{
            selectedApplicationIds: [],
            selected: emptyFunction,
            selectAll: emptyFunction,
          }}
          setSelectedStatus={onSelectStatuses}
          selectedStatus={statuses}
          paging={paging}
          openStatusUpdateModal={statusUpdateModal.open}
          openBulkIssuePDFModal={pdfGenerateModal.open}
          openCsvUploadModal={csvUploadModal.open}
          openCsvDownloadModal={downloadCsvModal.open}
          openBulkDownloadFilesModal={bulkDownloadFilesModal.open}
        />
        {applications.length === 0 &&
          matchType(
            !procedureId && !from && !to ? "no_registered" : "no_results",
            {
              no_registered: (
                <div className="py-2">
                  <InformationBanner
                    message="振り分けられた申請がまだありません。"
                    description="申請が振り分けられると、ここに表示されます。"
                  />
                </div>
              ),
              no_results: (
                <div className="py-2">
                  <InformationBanner
                    message="条件に該当する申請はありませんでした。"
                    description="絞り込み条件を確認してください。"
                  />
                </div>
              ),
            },
          )}
      </div>
      <div className="applications-pagination-footer">
        <Pagination
          onPageClick={onSetPage}
          currentPage={paging.current}
          lastPage={paging.total}
        />
      </div>
    </SmartApplyTemplate>
  );
};
